import { isExternalRouter } from 'auth/cognito';
import { AxiosError } from 'axios';
import { showErrorNotification, showNotification } from 'components/ShowNotification';
import {
    ProfileSkillsUpdatePayload,
    ResumeProfileModel,
} from 'generated-types/skills/resume_profile';
import {
    SkillsMemberSkillModelList,
    SkillsMemberSkillUpdatePayloadList,
} from 'generated-types/skills/skill';
import { useMemberSkillsHook } from 'hooks/skills/useMemberSkills';
import { useMyExternalProfile, useProfile } from 'hooks/useProfile';
import { Dispatch, SetStateAction, useState } from 'react';
import { Button, ButtonGroup, FlexboxGrid, Panel } from 'rsuite';

import SkillEdit, { type SkillEditModel } from './SkillEdit';
import { UseMutationResult } from 'react-query';

export const SkillsEdit = ({
    selectedResumeProfile,
    setSelectedResumeProfile,
    updateResumeProfileSkills,
    refetchResumeProfiles,
    memberSkills,
    onEditModeClosed,
}: {
    selectedResumeProfile: ResumeProfileModel;
    setSelectedResumeProfile: Dispatch<SetStateAction<ResumeProfileModel | undefined>>;
    updateResumeProfileSkills: UseMutationResult<
        ProfileSkillsUpdatePayload,
        AxiosError,
        ProfileSkillsUpdatePayload
    >;
    refetchResumeProfiles: () => void;
    memberSkills: SkillsMemberSkillModelList;
    onEditModeClosed: () => void;
}) => {
    const suggestedTopSkillValue = 12;

    const initialLevels: SkillEditModel[] = memberSkills.map<SkillEditModel>(skill => {
        return {
            id: skill.id,
            name: skill.name,
            level: skill.level,
            show_on_cv: selectedResumeProfile.skill_ids.includes(skill.id),
        };
    });

    const [levels, setLevels] = useState(initialLevels);

    const hookToUseForProfile = isExternalRouter ? useMyExternalProfile : useProfile;
    const { user } = hookToUseForProfile();
    const useMemberSkills = useMemberSkillsHook(isExternalRouter);
    const { updateMySkills, refetch } = useMemberSkills({
        enabled: false,
        member_id: user?.id,
    });

    const onSave = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const skillVisibilityPayload: ProfileSkillsUpdatePayload = {
            skill_ids: levels.filter(skill => skill.show_on_cv).map(skill => skill.id),
        };

        const skillLevelPayload: SkillsMemberSkillUpdatePayloadList = levels.map(skill => ({
            id: skill.id,
            level: skill.level,
        }));

        updateMySkills.mutate(skillLevelPayload, {
            onSuccess: () => {
                updateResumeProfileSkills.mutate(skillVisibilityPayload, {
                    onSuccess: data => {
                        onEditModeClosed();
                        showNotification({ header: 'Skills updated successfully' });
                        refetch();
                        setSelectedResumeProfile(data as ResumeProfileModel);
                    },
                    onError: (error: AxiosError) => {
                        showErrorNotification(error);
                    },
                });
            },
            onError: error => {
                showErrorNotification(error);
            },
        });
    };

    const onVisibilityChange = (index: number, visible: boolean) => {
        if (index >= 0) {
            const newLevels = levels.map((l, idx) => {
                if (idx === index) {
                    l.show_on_cv = visible;
                }
                return l;
            });
            setLevels([...newLevels]);
        }
    };

    const onSkillLevelChange = (index: number, level: number) => {
        if (index !== -1) {
            const newLevels = [...levels];
            if (level === 0) {
                newLevels[index].level = undefined;
                newLevels[index].show_on_cv = false;
            } else {
                newLevels[index].level = level;
            }
            setLevels(newLevels);
        }
    };

    const selectTopSkills = () => {
        const topSkills: SkillsMemberSkillModelList = Object.create(levels);
        topSkills
            .filter(skill => skill.level)
            .sort((a, b) => {
                if (!a.level) return 1;
                if (!b.level) return -1;
                if (a.level > b.level) return -1;
                return 1;
            })
            .forEach((skill, index) => {
                const skillIndex = levels.findIndex(l => l.id === skill.id);
                if (index < suggestedTopSkillValue) {
                    onVisibilityChange(skillIndex, true);
                } else {
                    onVisibilityChange(skillIndex, false);
                }
            });
    };

    const selectAllSkills = (select: boolean) => {
        levels.forEach((skill, index) => {
            if (skill?.level && skill.level > 0) {
                onVisibilityChange(index, select);
            }
        });
    };

    return (
        <form onSubmit={event => onSave(event)}>
            <FlexboxGrid justify="space-between">
                {levels.map((skill, idx) => {
                    return (
                        <SkillEdit
                            key={skill.id}
                            skill={skill}
                            skillIndex={idx}
                            changeSkillLevel={onSkillLevelChange}
                            changeVisibility={onVisibilityChange}
                        />
                    );
                })}
                <FlexboxGrid.Item colspan={24} style={{ textAlign: 'center' }}>
                    <Panel
                        bordered
                        style={{
                            marginBottom: 20,
                            textAlign: 'center',
                            width: 400,
                            marginLeft: 'auto',
                            marginRight: 'auto',
                        }}
                    >
                        <ButtonGroup>
                            <Button appearance="link" onClick={() => selectAllSkills(true)}>
                                Display all
                            </Button>
                            <Button appearance="link" onClick={() => selectTopSkills()}>
                                Display top ({suggestedTopSkillValue})
                            </Button>
                            <Button appearance="link" onClick={() => selectAllSkills(false)}>
                                Hide all
                            </Button>
                        </ButtonGroup>
                    </Panel>
                    <Button
                        type="submit"
                        appearance="primary"
                        color="green"
                        style={{ width: 300 }}
                        loading={updateMySkills.isLoading}
                    >
                        Save
                    </Button>
                </FlexboxGrid.Item>
            </FlexboxGrid>
        </form>
    );
};
